/*
 * The palette, for the places that need a colour in JavaScript - an inline
 * style, a chart library, a prop passed down to a component.
 *
 * Every value points at the custom property of the same name in
 * styles/variables.css rather than repeating its hex. That is what lets a theme
 * change reach an inline style at all: `style={{color: Colors.red}}` becomes
 * `color: var(--ds-danger-600)`, which is resolved by the browser at paint time
 * against whatever the current theme has put in that property. A hex baked in
 * here could never follow it.
 *
 * Two rules come with that:
 *
 *   - These belong in a CSS context - a style object, a CSS string. They are
 *     not colours a script can read and compute with: `Colors.red` is the text
 *     "var(--ds-danger-600)", not "#d9352e". Canvas, and anything that parses a
 *     colour itself, needs the resolved value - read it with getComputedStyle.
 *
 *   - Not in an SVG presentation attribute. `stroke={Colors.red}` puts the text
 *     into the attribute, where custom-property substitution is not something to
 *     rely on across browsers, and where failing means an icon with no stroke at
 *     all. Icons take stroke="currentColor" and the colour comes from `color` on
 *     the element - which is plain CSS, and themeable for free.
 */

const scale = {
    brand900: 'var(--ds-brand-900)',
    brand800: 'var(--ds-brand-800)',
    brand600: 'var(--ds-brand-600)',
    brand400: 'var(--ds-brand-400)',
    brand100: 'var(--ds-brand-100)',
    brand50: 'var(--ds-brand-50)',
    brand25: 'var(--ds-brand-25)',

    /* the brighter blue the chat already uses for a message of one's own -
       in the brand family but clearly apart from brand800, which is what makes
       it readable as "this one is different" rather than "this one is wrong" */
    utilityBrand500: 'var(--ds-utility-brand-500)',

    danger800: 'var(--ds-danger-800)',
    danger600: 'var(--ds-danger-600)',
    danger300: 'var(--ds-danger-300)',
    danger50: 'var(--ds-danger-50)',

    neutral900: 'var(--ds-neutral-900)',
    neutral600: 'var(--ds-neutral-600)',
    neutral400: 'var(--ds-neutral-400)',
    neutral200: 'var(--ds-neutral-200)',
    neutral50: 'var(--ds-neutral-50)',

    success600: 'var(--ds-success-600)',
    success50: 'var(--ds-success-50)',

    warning800: 'var(--ds-warning-800)',
    warning500: 'var(--ds-warning-500)',
    warning50: 'var(--ds-warning-50)',
};

/*
 * The circle someone with no photo is drawn in. An array rather than named
 * entries, because the only thing a caller does with these is pick one by
 * index - see pickAvatarColor in ContactAvatar.
 */
const avatarPalette = [
    'var(--ds-avatar-1)',
    'var(--ds-avatar-2)',
    'var(--ds-avatar-3)',
    'var(--ds-avatar-4)',
    'var(--ds-avatar-5)',
];

const colors = {
    avatarPalette,

    ...scale,

    /* --- the names the app already uses, pointing at the scale above ------- */

    blue: scale.brand800,
    blueDark: scale.brand800,
    blueAccent: scale.utilityBrand500,
    blueLite: scale.brand600,
    blueExtraLite: scale.brand50,
    blueExtraLite2: scale.brand50,
    blueExtraLite3: scale.neutral50,

    red: scale.danger600,
    redSemiLite: scale.danger600,
    redLite: scale.danger300,
    redExtraLite: scale.danger50,

    silverExtraDark: scale.neutral600,
    silverDark: scale.neutral600,
    silver: scale.neutral200,
    silverSemiLite: scale.neutral200,
    silverLite: scale.neutral50,
    silverExtraLite: scale.neutral50,

    silverBlueDark: scale.neutral600,
    silverBlue: scale.neutral400,
    silverBlueSemiLite: scale.neutral400,
    silverBlueLite: scale.neutral400,
    silverBlueExtraLite: scale.neutral200,
    silverWhiteSemiDark: scale.neutral600,

    green: scale.success600,
    /* four files ask for colors.success, which never existed here - the icon
       they pass it to has been drawing in its inherited colour all along */
    success: scale.success600,

    yellow: scale.warning500,
    yellowExtraDark: scale.warning800,
    yellowSemiLite: scale.warning50,
    yellowExtraLite: scale.warning50,
};

export default colors;



// WEBPACK FOOTER //
// ./src/utils/Colors.js